import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import {MuiRFF, Yup} from '../../form';
import {CheckboxForm} from "../../base/checkbox/checkbox";
import Api from "../../../../core/services/api/api";


const formNames = {
    title: 'title',
    isSuggestiveSellingEnabled: 'isSuggestiveSellingEnabled',
    automaticallyAddedToCart: 'automaticallyAddedToCart',
};

const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formNames.isSuggestiveSellingEnabled]: Yup.boolean().nullable().default(false),
    // [formNames.automaticallyAddedToCart]: Yup.boolean().nullable().default(false),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const UpsertProductRelationTypeDialog = ({open, setOpen, relationType, onSuccess}) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.upsert-product-relation-type'});
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const inEditMode = useMemo(() => !!relationType?.id, [relationType?.id]);

    const initialValues = useMemo(() => ({
        [formNames.title]: relationType?.title ?? '',
        [formNames.isSuggestiveSellingEnabled]: relationType?.isSuggestiveSellingEnabled ?? relationType?.functionality === "Cross-Sale",
        // [formNames.automaticallyAddedToCart]: relationType?.automaticallyAddedToCart ?? false,
    }), [relationType?.functionality, relationType?.isSuggestiveSellingEnabled, relationType?.title]);

    /**
     * Creates the relation type
     * @type {(function(*): Promise<void>)|*}
     */
    const createRelationType = useCallback(async (values) => {
        const forApi = {
            title: values[formNames.title]?.trim(),
            functionality: !!values[formNames.isSuggestiveSellingEnabled] ? "Cross-Sale" : null,
            // automaticallyAddedToCart: !!values[formNames.isSuggestiveSellingEnabled] && !!values[formNames.automaticallyAddedToCart],
        };
        const response = await Api.addNewPartRelationType(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onSuccess(response.data);
            setOpen(false);
        }
    }, [isMounted, onSuccess, setOpen]);

    /**
     * Updates the relation type.
     * @type {(function(*): Promise<void>)|*}
     */
    const updateRelationType = useCallback(async (values) => {
        const forApi = {
            id: relationType.id,
            title: values[formNames.title]?.trim(),
            functionality: !!values[formNames.isSuggestiveSellingEnabled] ? "Cross-Sale" : null,
            // automaticallyAddedToCart: !!values[formNames.isSuggestiveSellingEnabled] && !!values[formNames.automaticallyAddedToCart],
        };
        const response = await Api.updatePartRelationType(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onSuccess(response.data ?? forApi);
            setOpen(false);
        }
    }, [isMounted, onSuccess, relationType?.id, setOpen]);

    /**
     * Submits the form.
     * @type {(function(*): Promise<void>)|*}
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        if (inEditMode) {
            await updateRelationType(values);
        } else {
            await createRelationType(values);
        }
        if (!isMounted())
            return;
        setSubmitting(false);
    }, [createRelationType, inEditMode, isMounted, updateRelationType]);

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
            loading={submitting}
            disableEscapeKeyDown={submitting}
            disableBackdropClick={submitting}
            className={'bound-dialog'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    {inEditMode ? t('editTitle') : t('addTitle')}
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent className={'px-2 pt-4'}>
                <Col xs={12} className={'mb-4'}>
                    <Form
                        className='d-flex flex-wrap w-100 align-items-start'
                        onSubmit={onSubmit}
                        id={'upsert-product-relation-type-form'}
                        validate={validate}
                        initialValues={initialValues}
                        render={({values, form}) => (
                            <>
                                <Col xs={12} className={'p-0 mb-3'}>
                                    <OutlinedInputForm
                                        fullWidth
                                        name={formNames.title}
                                        placeholder={t('titlePlaceholder')}
                                        label={t('titleLabel')}
                                        className={'mr-2'}
                                        required={required[formNames.title]}
                                        value={values[formNames.title] ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                    />
                                </Col>
                                <Col xs={12}>
                                    <CheckboxForm
                                        name={formNames.isSuggestiveSellingEnabled}
                                        data={{
                                            value: !!values[formNames.isSuggestiveSellingEnabled],
                                            label: t('isSuggestiveSellingEnabled'),
                                        }}
                                    />
                                    {/*{*/}
                                    {/*    !!values[formNames.isSuggestiveSellingEnabled] &&*/}
                                    {/*    <CheckboxForm*/}
                                    {/*        name={formNames.automaticallyAddedToCart}*/}
                                    {/*        data={{*/}
                                    {/*            value: !!values[formNames.automaticallyAddedToCart],*/}
                                    {/*            label: t('automaticallyAddedToCart'),*/}
                                    {/*        }}*/}
                                    {/*    />*/}
                                    {/*}*/}
                                </Col>
                            </>
                        )}
                    />
                </Col>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Row className={'w-100 no-gutters'}>
                    <Divider className='divider'/>
                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                        <Button
                            disabled={!open || submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => setOpen(false)}
                            borderRadius={'0 8px 0 8px'}
                        >
                            {t('close')}
                        </Button>
                        <Button
                            disabled={!open || submitting}
                            variant={'dialog'}
                            color={'primary'}
                            type={'submit'}
                            form={'upsert-product-relation-type-form'}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {submitting ? t('submitting') : t('submit')}
                        </Button>
                    </Row>
                </Row>
            </DialogActions>
        </Dialog>
    );
};

export default UpsertProductRelationTypeDialog;
